import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ShieldAlert, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { getFingerprint, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BidReceipt } from "./BidReceipt";

export const SealedBidPanel = ({ auction, onBidPlaced }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [amount, setAmount] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [receipt, setReceipt] = useState(null);

    const isOpen = auction.status === "OPEN";
    const alreadyBid = !!auction.my_bid;

    const eligibility = [
        { ok: !!user, label: "Signed in" },
        { ok: user?.email_verified, label: "Email verified" },
        { ok: user?.phone_verified, label: "Phone verified" },
    ];
    const eligible = eligibility.every((e) => e.ok);

    const submit = async () => {
        if (!user) { navigate("/login"); return; }
        if (!eligible) { navigate("/account/verify"); return; }
        const amt = parseFloat(amount);
        if (!amt || amt < auction.starting_price) {
            toast.error(`Bid must be at least ${money(auction.starting_price)}`);
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/auctions/${auction.id}/bid`, {
                amount: amt,
                device_fingerprint: getFingerprint(),
            });
            setReceipt(data);
            toast.success("Sealed bid recorded");
            if (onBidPlaced) onBidPlaced();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not place bid");
        } finally {
            setSubmitting(false);
        }
    };

    if (receipt) {
        return (
            <div data-testid="sealed-bid-panel" className="rounded-xl border border-border bg-card p-5">
                <BidReceipt receiptId={receipt.receipt_id} amount={receipt.amount} createdAt={receipt.created_at} />
            </div>
        );
    }

    if (alreadyBid) {
        return (
            <div data-testid="sealed-bid-panel" className="rounded-xl border border-border bg-card p-5">
                <BidReceipt receiptId={auction.my_bid.id} amount={auction.my_bid.amount} createdAt={auction.my_bid.created_at} />
            </div>
        );
    }

    return (
        <div data-testid="sealed-bid-panel" className="rounded-xl border border-border bg-card p-5">
            <div className="mb-1 flex items-center gap-2">
                <Lock className="h-4 w-4 text-primary" aria-hidden />
                <h3 className="font-display text-base font-semibold">Place your sealed bid</h3>
            </div>
            <p id="sealed-rule" className="mb-4 text-xs text-muted-foreground">
                Your bid is sealed. You can submit exactly <strong>one bid</strong> for this item. After submission you cannot edit it or view competing bids.
            </p>

            {!isOpen ? (
                <div className="rounded-lg border border-border bg-muted p-3 text-sm text-muted-foreground">
                    This auction is closed. Bidding is no longer available.
                </div>
            ) : (
                <>
                    <div className="space-y-2">
                        <Label htmlFor="bid-amount">Your bid (USD)</Label>
                        <div className="relative">
                            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <Input
                                id="bid-amount"
                                data-testid="sealed-bid-amount-input"
                                type="number"
                                min={auction.starting_price}
                                step="0.01"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                aria-describedby="sealed-rule"
                                placeholder={`${auction.starting_price}`}
                                className="pl-7 tabular-nums"
                            />
                        </div>
                        <p className="text-xs text-muted-foreground">Minimum bid: {money(auction.starting_price)}</p>
                    </div>

                    {user && !eligible && (
                        <div className="mt-4 rounded-lg border border-warning/40 bg-warning/10 p-3">
                            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-warning">
                                <ShieldAlert className="h-4 w-4" /> Verification required to bid
                            </div>
                            <ul className="space-y-1 text-xs">
                                {eligibility.map((e) => (
                                    <li key={e.label} className="flex items-center gap-2">
                                        <CheckCircle2 className={`h-3.5 w-3.5 ${e.ok ? "text-success" : "text-muted-foreground/40"}`} />
                                        <span className={e.ok ? "text-muted-foreground line-through" : ""}>{e.label}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <Button
                        data-testid="sealed-bid-submit-button"
                        className="mt-4 w-full rounded-[10px]"
                        onClick={submit}
                        disabled={submitting}
                    >
                        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                        {!user ? "Sign in to bid" : !eligible ? "Verify account to bid" : "Submit sealed bid"}
                    </Button>
                </>
            )}
        </div>
    );
};
